import { apiClient, ApiError, getAuthToken } from '@/shared/data/client';
import { mapProjectCsvMappingDto } from '@/shared/data/dtoMappers';
import { getErrorMessage } from '@/shared/data/errors';
import type { CsvHeaderAliases, CsvStatusAliases, ProjectCsvMappingDto } from '@flowqa/shared';

type ProjectCsvMappingUpdateInput = {
  projectId: string;
  headerAliases: CsvHeaderAliases;
  statusAliases: CsvStatusAliases;
};

const toApiError = (err: unknown): ApiError =>
  err instanceof ApiError ? err : new ApiError(getErrorMessage(err));

const ensureToken = (tokenOverride?: string | null): string => {
  const token = tokenOverride ?? getAuthToken();
  if (!token) {
    throw new ApiError('Not authenticated');
  }
  return token;
};

export const getProjectCsvMapping = async (
  projectId: string,
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<ProjectCsvMappingDto> => {
  const token = ensureToken(tokenOverride);
  try {
    const result = await apiClient.get<ProjectCsvMappingDto>(`projects/${projectId}/csv-mapping`, {
      token,
      retries: 1,
      orgId: orgId ?? undefined,
    });
    return mapProjectCsvMappingDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};

export const updateProjectCsvMapping = async (
  input: ProjectCsvMappingUpdateInput,
  orgId?: string | null,
  tokenOverride?: string | null,
): Promise<ProjectCsvMappingDto> => {
  const token = ensureToken(tokenOverride);
  try {
    const result = await apiClient.put<ProjectCsvMappingDto>(
      `projects/${input.projectId}/csv-mapping`,
      { headerAliases: input.headerAliases, statusAliases: input.statusAliases },
      { token, retries: 1, orgId: orgId ?? undefined },
    );
    return mapProjectCsvMappingDto(result);
  } catch (err) {
    throw toApiError(err);
  }
};
